// src/hooks/useBulkOrder.js
import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { getShops } from '../services/api/shops';
import { bulkCreateOrders } from '../services/api/orders';
import { bulkAssignCreatedOrders } from '../services/api/orderAssignments';
import { generateBulkOrderTemplate } from '../utils/excelTemplateGenerator';
import { formatPhoneNumbers, splitRawPhoneInput } from '../utils/phoneNumberFormatter';
import { resolveColumn } from '../utils/bulkColumnMapping';
import { useAuth } from './useAuth';

const CHUNK_SIZE = 50;

const CANONICAL_PHONE_FIELDS = ['Phone_1', 'Phone_2', 'Phone_3'];

// City-not-found error patterns the backend returns
const CITY_NOT_FOUND_PATTERNS = [
  /city .* not found/i,
  /city not found/i,
  /invalid city/i,
];

const isCityError = (errorMsg) =>
  CITY_NOT_FOUND_PATTERNS.some(re => re.test(errorMsg));

// Extract a phone-like number embedded in any text field value
const PHONE_IN_TEXT_RE =
  /\+94[\s\-]?\d[\d\s\-]{7,11}|0094[\s\-]?\d[\d\s\-]{7,11}|0\d[\d\s\-]{7,9}|\b\d{9,10}\b/;

const extractPhoneFromText = (text) => {
  if (!text) return '';
  const match = text.toString().match(PHONE_IN_TEXT_RE);
  return match ? match[0].replace(/[\s\-]/g, '') : '';
};

const useBulkOrder = () => {
  const { user } = useAuth();

  const [selectedShop,          setSelectedShop]          = useState(null);
  const [selectedShiftId,       setSelectedShiftId]       = useState(null);
  const [uploadedFile,          setUploadedFile]           = useState(null);
  const [parsedData,            setParsedData]             = useState([]);
  const [validationResults,     setValidationResults]      = useState(null);
  const [submissionResults,     setSubmissionResults]      = useState(null);
  const [isProcessing,          setIsProcessing]           = useState(false);
  const [processingStep,        setProcessingStep]         = useState('select');
  const [progress,              setProgress]               = useState({ current: 0, total: 0 });
  const [userShops,             setUserShops]              = useState([]);
  const [selectedAssignUserIds, setSelectedAssignUserIds]  = useState([]);
  const [defaultProduct,        setDefaultProduct]         = useState(null);
  const [useCityUnknown,        setUseCityUnknown]         = useState(false);
  const [duplicateCheckDays,    setDuplicateCheckDays]     = useState(2);
  const [columnMappings,        setColumnMappings]         = useState({});
  const [duplicateRows,         setDuplicateRows]          = useState({});
  const [blacklistedRowNumbers, setBlacklistedRowNumbers]  = useState(new Set());

  // ── Load shops ────────────────────────────────────────────────────────────
  const loadShops = useCallback(async () => {
    try {
      const shops = await getShops();
      setUserShops(shops || []);
    } catch (error) {
      console.error('Failed to load shops:', error);
    }
  }, []);

  // ── Parse Excel ───────────────────────────────────────────────────────────
  const parseExcelFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data     = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          let sheetName = 'Orders';
          if (!workbook.SheetNames.includes(sheetName)) {
            sheetName = 'Sheet1';
            if (!workbook.SheetNames.includes(sheetName)) {
              sheetName = workbook.SheetNames[0];
            }
          }

          const worksheet = workbook.Sheets[sheetName];
          const jsonData  = XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: null });

          const businessCountryCode = user?.business?.countryCode || 'LK';

          const transformedData = jsonData.map((row, index) => {
            const r = (canonicalKey) => resolveColumn(row, canonicalKey, columnMappings);

            const transformed = {
              rowNumber:          index + 2,
              originalRow:        { ...row },
              customerName:       r('Customer_Name')     || '',
              customerEmail:      r('Customer_Email')    || null,
              address:            r('Address')           || '',
              cityName:           r('City_Name')         || '',
              paymentType:        row['Payment_Type']    || 'cash_on_delivery',
              orderStatus:        row['Order_Status']    || 'pending',
              privateReference:   r('Private_Reference') || '',
              deliveryNotes:      r('Delivery_Notes')    || null,
              specialNotes:       row['Special_Notes']   || null,
              deliveryCharge:     row['Delivery_Charge'] || null,
              totalDiscount:      row['Total_Discount']  || null,
              phoneNumbers:       [],
              rawPhones:          [],
              items:              [],
              cityWasUnknown:     false,
              usedDefaultProduct: false,
            };

            // ── Phones ────────────────────────────────────────────────────
            const phone1Raw = (r('Phone_1') ?? '').toString().trim();
            const phone2Raw = (r('Phone_2') ?? '').toString().trim();
            const phone3Raw = (r('Phone_3') ?? '').toString().trim();

            // Phone_1 may contain two numbers (comma/space/concatenated).
            // splitRawPhoneInput extracts up to 2 tokens.
            const phone1Parts = splitRawPhoneInput(phone1Raw, businessCountryCode);
            const collectedRaw = [];

            if (phone1Parts[0]) collectedRaw.push(phone1Parts[0]);
            if (phone1Parts[1]) collectedRaw.push(phone1Parts[1]);

            // Compare by last N significant digits so that "0782848268",
            // "782848268", "+94782848268" all match each other.
            const phoneDigitSig = (raw) =>
              raw.replace(/\D/g, '').replace(/^0+/, '').slice(-9);
            const isDuplicatePhone = (candidate) => {
              const sig = phoneDigitSig(candidate);
              return sig.length > 4 && collectedRaw.some(p => phoneDigitSig(p) === sig);
            };

            // Add Phone_2 field if it has digits and isn't already collected
            if (phone2Raw && /\d/.test(phone2Raw) && !isDuplicatePhone(phone2Raw)) {
              collectedRaw.push(phone2Raw);
            }

            // If we still only have 1 phone, try extracting from text fields —
            // but only if the extracted number isn't the same as phone1
            if (collectedRaw.length < 2) {
              const textExtracted =
                extractPhoneFromText(transformed.customerName) ||
                extractPhoneFromText(transformed.address)      ||
                extractPhoneFromText(transformed.cityName)     || '';
              if (textExtracted && !isDuplicatePhone(textExtracted)) {
                collectedRaw.push(textExtracted);
              }
            }

            // Add Phone_3 if present, has digits, and isn't a duplicate
            if (phone3Raw && /\d/.test(phone3Raw) && !isDuplicatePhone(phone3Raw)) {
              collectedRaw.push(phone3Raw);
            }

            const rawPhones = collectedRaw;
            transformed.rawPhones = rawPhones;

            if (rawPhones.length > 0) {
              const phoneResult        = formatPhoneNumbers(rawPhones, businessCountryCode);
              transformed.phoneNumbers = phoneResult.formattedNumbers;
              transformed.phoneErrors  = phoneResult.errors;
            } else {
              transformed.phoneNumbers = [];
              transformed.phoneErrors  = [];
            }

            // ── Items ─────────────────────────────────────────────────────
            let hasValidItem = false;
            for (let i = 1; i <= 5; i++) {
              const sku      = row[`Item_${i}_SKU`];
              const quantity = row[`Item_${i}_Quantity`];
              const custom   = row[`Item_${i}_Custom_Line_Total`];
              if (sku && quantity) {
                hasValidItem = true;
                transformed.items.push({
                  sku:             sku.toString().trim(),
                  quantity:        parseInt(quantity) || 1,
                  customLineTotal: custom ? parseFloat(custom) : null,
                });
              }
            }

            // Default product fallback
            if (!hasValidItem && defaultProduct) {
              transformed.items.push({
                sku:             defaultProduct.sku,
                quantity:        defaultProduct.quantity || 1,
                customLineTotal: null,
              });
              transformed.usedDefaultProduct = true;
            }

            // City Unknown fallback (empty city at parse time)
            if (!transformed.cityName && useCityUnknown) {
              transformed.cityName      = 'CITY UNKNOWN';
              transformed.cityWasUnknown = true;
            }

            return transformed;
          });

          resolve(transformedData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }, [user, columnMappings, defaultProduct, useCityUnknown]);

  // ── Handle file upload ────────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    setIsProcessing(true);
    setProcessingStep('upload');
    try {
      const parsed = await parseExcelFile(file);
      setUploadedFile(file);
      setParsedData(parsed);
      setValidationResults(null);
      setDuplicateRows({});
      setProcessingStep('validate');
    } catch (error) {
      console.error('File parsing error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [parseExcelFile]);

  const mapToOrderPayload = (data) => ({
    rowNumber:        data.rowNumber,
    customerName:     data.customerName,
    customerEmail:    data.customerEmail,
    address:          data.address,
    cityName:         data.cityName,
    paymentType:      data.paymentType,
    orderStatus:      data.orderStatus,
    privateReference: data.privateReference,
    deliveryNotes:    data.deliveryNotes,
    specialNotes:     data.specialNotes,
    phoneNumbers:     data.phoneNumbers,
    items:            data.items,
    deliveryCharge:   data.deliveryCharge,
    totalDiscount:    data.totalDiscount,
  });

  const chunkArray = (array) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += CHUNK_SIZE) chunks.push(array.slice(i, i + CHUNK_SIZE));
    return chunks;
  };

  /**
   * After the first validation pass, if useCityUnknown is enabled, find rows
   * whose ONLY errors are city-related. For those rows:
   *   1. Update parsedData to set cityName = 'CITY UNKNOWN', cityWasUnknown = true
   *   2. Re-run validation on just those rows and merge results back
   *
   * Returns { updatedParsedData, mergedResults }
   */
  const resolveCityUnknownRows = useCallback(async (
    currentParsedData,
    firstPassResults,
    shopId,
  ) => {
    if (!useCityUnknown) return { updatedParsedData: currentParsedData, mergedResults: firstPassResults };

    // Find rows where ALL errors are city-related
    const cityOnlyFailedRows = firstPassResults.filter(r => {
      if (r.status !== 'failed') return false;
      const errors = r.errors || [];
      return errors.length > 0 && errors.every(e => isCityError(e));
    });

    if (cityOnlyFailedRows.length === 0) {
      return { updatedParsedData: currentParsedData, mergedResults: firstPassResults };
    }

    const cityFailRowNumbers = new Set(cityOnlyFailedRows.map(r => r.rowNumber));

    // Update parsedData for those rows
    const updatedParsedData = currentParsedData.map(row => {
      if (!cityFailRowNumbers.has(row.rowNumber)) return row;
      return { ...row, cityName: 'CITY UNKNOWN', cityWasUnknown: true };
    });

    // Re-validate just those rows
    const rowsToRetry = updatedParsedData
      .filter(d => cityFailRowNumbers.has(d.rowNumber))
      .map(mapToOrderPayload);

    const retryChunks  = chunkArray(rowsToRetry);
    const retryResults = [];
    let retryDuplicates = {};

    for (const chunk of retryChunks) {
      const chunkResult = await bulkCreateOrders(shopId, chunk, true, false, duplicateCheckDays, selectedShiftId);
      retryResults.push(...chunkResult.results);
      if (chunkResult.duplicates) {
        retryDuplicates = { ...retryDuplicates, ...chunkResult.duplicates };
      }
    }

    // Merge: replace city-only failures with retry results
    const mergedResults = firstPassResults.map(r => {
      if (!cityFailRowNumbers.has(r.rowNumber)) return r;
      return retryResults.find(rr => rr.rowNumber === r.rowNumber) || r;
    });

    return { updatedParsedData, mergedResults, retryDuplicates };
  }, [useCityUnknown, duplicateCheckDays, selectedShiftId]);

  // ── Validation ────────────────────────────────────────────────────────────
  const handleValidation = useCallback(async () => {
    if (!selectedShop || parsedData.length === 0) return;
    setIsProcessing(true);
    setProcessingStep('validate');
    setProgress({ current: 0, total: parsedData.length });
    setDuplicateRows({});

    try {
      const orders  = parsedData.map(mapToOrderPayload);
      const chunks  = chunkArray(orders);
      const firstPassResults = [];
      let firstPassDuplicates = {};

      for (let i = 0; i < chunks.length; i++) {
        const chunkResult = await bulkCreateOrders(
          selectedShop.id,
          chunks[i],
          true,
          false,
          duplicateCheckDays,
          selectedShiftId,
        );
        firstPassResults.push(...chunkResult.results);
        if (chunkResult.duplicates) {
          firstPassDuplicates = { ...firstPassDuplicates, ...chunkResult.duplicates };
        }
        setProgress({ current: firstPassResults.length, total: orders.length });
      }

      // ── City Unknown resolution ──────────────────────────────────────────
      const { updatedParsedData, mergedResults, retryDuplicates = {} } = await resolveCityUnknownRows(
        parsedData,
        firstPassResults,
        selectedShop.id,
      );

      if (updatedParsedData !== parsedData) {
        setParsedData(updatedParsedData);
      }

      const validCount      = mergedResults.filter(r => r.status === 'valid').length;
      const failureCount    = mergedResults.filter(r => r.status === 'failed').length;
      const blacklistedSet  = new Set(mergedResults.filter(r => r.isBlacklisted).map(r => r.rowNumber));

      setBlacklistedRowNumbers(blacklistedSet);
      setValidationResults({
        summary: { totalSubmitted: mergedResults.length, validCount, failureCount, blacklistedCount: blacklistedSet.size },
        results: mergedResults,
      });

      setDuplicateRows({ ...firstPassDuplicates, ...retryDuplicates });
      setProgress({ current: mergedResults.length, total: mergedResults.length });
    } catch (error) {
      console.error('Validation error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedShop, parsedData, duplicateCheckDays, selectedShiftId, resolveCityUnknownRows]);

  // ── Retry validation ──────────────────────────────────────────────────────
  const handleRetryValidation = useCallback(async () => {
    if (!selectedShop || !validationResults) return;
    setIsProcessing(true);
    setProcessingStep('validate');

    try {
      const invalidRowNumbers = validationResults.results
        .filter(r => r.status === 'failed')
        .map(r => r.rowNumber);

      const ordersToRetry = parsedData
        .filter(d => invalidRowNumbers.includes(d.rowNumber))
        .map(mapToOrderPayload);

      setProgress({ current: 0, total: ordersToRetry.length });

      const chunks            = chunkArray(ordersToRetry);
      const allRetryResults   = [];
      let   allRetryDuplicates = {};

      for (const chunk of chunks) {
        const chunkResult = await bulkCreateOrders(selectedShop.id, chunk, true, false, duplicateCheckDays, selectedShiftId);
        allRetryResults.push(...chunkResult.results);
        if (chunkResult.duplicates) {
          allRetryDuplicates = { ...allRetryDuplicates, ...chunkResult.duplicates };
        }
        setProgress({ current: allRetryResults.length, total: ordersToRetry.length });
      }

      // City Unknown resolution on retry results too
      const { updatedParsedData, mergedResults: resolvedRetry, retryDuplicates = {} } = await resolveCityUnknownRows(
        parsedData,
        allRetryResults,
        selectedShop.id,
      );

      if (updatedParsedData !== parsedData) setParsedData(updatedParsedData);

      // Merge duplicates: keep existing non-retried rows' duplicates, add new ones from retry
      setDuplicateRows(prev => ({ ...prev, ...allRetryDuplicates, ...retryDuplicates }));

      const mergedResults = validationResults.results.map(oldResult => {
        if (oldResult.status === 'valid') return oldResult;
        return resolvedRetry.find(r => r.rowNumber === oldResult.rowNumber) || oldResult;
      });

      const validCount      = mergedResults.filter(r => r.status === 'valid').length;
      const failureCount    = mergedResults.filter(r => r.status === 'failed').length;
      const blacklistedSet  = new Set(mergedResults.filter(r => r.isBlacklisted).map(r => r.rowNumber));

      setBlacklistedRowNumbers(blacklistedSet);
      setValidationResults({
        ...validationResults,
        summary: { ...validationResults.summary, validCount, failureCount, blacklistedCount: blacklistedSet.size },
        results: mergedResults,
      });
    } catch (error) {
      console.error('Retry validation error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedShop, parsedData, validationResults, duplicateCheckDays, selectedShiftId, resolveCityUnknownRows]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!selectedShop || !validationResults) return;
    setIsProcessing(true);
    setProcessingStep('submit');

    try {
      const validRowNumbers     = validationResults.results.filter(r => r.status === 'valid').map(r => r.rowNumber);
      const duplicateRowNumbers = new Set(Object.keys(duplicateRows).map(Number));

      const ordersToSubmit = parsedData
        .filter(d => validRowNumbers.includes(d.rowNumber) && !duplicateRowNumbers.has(d.rowNumber))
        .map(mapToOrderPayload);

      setProgress({ current: 0, total: ordersToSubmit.length });

      const hasManualAssignment = selectedAssignUserIds.length > 0;
      const chunks              = chunkArray(ordersToSubmit);
      const allResults          = [];

      for (const chunk of chunks) {
        const chunkResult = await bulkCreateOrders(
          selectedShop.id, chunk, false, hasManualAssignment, 0, selectedShiftId,
        );
        allResults.push(...chunkResult.results);
        setProgress({ current: allResults.length, total: ordersToSubmit.length });
      }

      const successCount = allResults.filter(r => r.status === 'success').length;
      const failureCount = allResults.filter(r => r.status === 'failed').length;

      if (hasManualAssignment && successCount > 0) {
        const createdOrderIds = allResults
          .filter(r => r.status === 'success' && r.orderId)
          .map(r => r.orderId);

        if (createdOrderIds.length > 0) {
          try {
            await bulkAssignCreatedOrders({
              orderIds: createdOrderIds,
              userIds:  selectedAssignUserIds,
              shopId:   selectedShop.id,
            });
          } catch (assignErr) {
            console.error('Bulk assignment after creation failed:', assignErr);
          }
        }
      }

      setSubmissionResults({
        success:           failureCount === 0,
        summary:           { totalSubmitted: ordersToSubmit.length, successCount, failureCount },
        results:           allResults,
        manuallyAssigned:  hasManualAssignment && successCount > 0,
        assignedUserCount: selectedAssignUserIds.length,
      });
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedShop, parsedData, validationResults, selectedAssignUserIds, duplicateRows, selectedShiftId]);

  // ── Remove all duplicates ─────────────────────────────────────────────────
  const removeAllDuplicates = useCallback(() => {
    const dupRowNumbers = new Set(Object.keys(duplicateRows).map(Number));
    if (dupRowNumbers.size === 0) return;

    setParsedData(prev => prev.filter(d => !dupRowNumbers.has(d.rowNumber)));
    setDuplicateRows({});
    setBlacklistedRowNumbers(prev => new Set([...prev].filter(n => !dupRowNumbers.has(n))));

    if (validationResults) {
      const newResults      = validationResults.results.filter(r => !dupRowNumbers.has(r.rowNumber));
      const validCount      = newResults.filter(r => r.status === 'valid').length;
      const failureCount    = newResults.filter(r => r.status === 'failed').length;
      const blacklistedCount = newResults.filter(r => r.isBlacklisted).length;
      setValidationResults({
        ...validationResults,
        summary: { ...validationResults.summary, totalSubmitted: newResults.length, validCount, failureCount, blacklistedCount },
        results: newResults,
      });
    }
  }, [duplicateRows, validationResults]);

  // ── Remove single row ─────────────────────────────────────────────────────
  const removeInvalidRow = useCallback((rowNumber) => {
    setParsedData(prev => prev.filter(d => d.rowNumber !== rowNumber));
    setDuplicateRows(prev => { const next = { ...prev }; delete next[rowNumber]; return next; });
    setBlacklistedRowNumbers(prev => { const next = new Set(prev); next.delete(rowNumber); return next; });

    if (validationResults) {
      const newResults       = validationResults.results.filter(r => r.rowNumber !== rowNumber);
      const validCount       = newResults.filter(r => r.status === 'valid').length;
      const failureCount     = newResults.filter(r => r.status === 'failed').length;
      const blacklistedCount = newResults.filter(r => r.isBlacklisted).length;
      setValidationResults({
        ...validationResults,
        summary: { ...validationResults.summary, totalSubmitted: newResults.length, validCount, failureCount, blacklistedCount },
        results: newResults,
      });
    }
  }, [validationResults]);

  // ── Remove all blacklisted rows ───────────────────────────────────────────
  const removeBlacklistedRows = useCallback(() => {
    if (blacklistedRowNumbers.size === 0) return;

    setParsedData(prev => prev.filter(d => !blacklistedRowNumbers.has(d.rowNumber)));
    setDuplicateRows(prev => {
      const next = { ...prev };
      blacklistedRowNumbers.forEach(n => delete next[n]);
      return next;
    });
    setBlacklistedRowNumbers(new Set());

    if (validationResults) {
      const newResults   = validationResults.results.filter(r => !blacklistedRowNumbers.has(r.rowNumber));
      const validCount   = newResults.filter(r => r.status === 'valid').length;
      const failureCount = newResults.filter(r => r.status === 'failed').length;
      setValidationResults({
        ...validationResults,
        summary: { ...validationResults.summary, totalSubmitted: newResults.length, validCount, failureCount, blacklistedCount: 0 },
        results: newResults,
      });
    }
  }, [blacklistedRowNumbers, validationResults]);

  // ── Update row ────────────────────────────────────────────────────────────
  const updateInvalidRow = useCallback((rowNumber, field, value) => {
    setParsedData(prev => prev.map(data => {
      if (data.rowNumber !== rowNumber) return data;
      const updated = { ...data, [field]: value };
      if (field === 'phoneNumbers' && Array.isArray(value)) {
        const businessCountryCode  = user?.business?.countryCode || 'LK';
        const phoneResult          = formatPhoneNumbers(value, businessCountryCode);
        updated.phoneNumbers       = phoneResult.formattedNumbers;
        updated.phoneErrors        = phoneResult.errors;
      }
      return updated;
    }));
  }, [user]);

  // ── Clear all ─────────────────────────────────────────────────────────────
  const clearAll = useCallback(() => {
    setUploadedFile(null);
    setParsedData([]);
    setValidationResults(null);
    setSubmissionResults(null);
    setProgress({ current: 0, total: 0 });
    setProcessingStep('select');
    setSelectedAssignUserIds([]);
    setDuplicateRows({});
    setBlacklistedRowNumbers(new Set());
    setDefaultProduct(null);
    setUseCityUnknown(false);
    setSelectedShiftId(null);
  }, []);

  // ── Download template ─────────────────────────────────────────────────────
  const downloadTemplate = useCallback(async (options) => {
    if (!selectedShop) throw new Error('Please select a shop first');

    const workbook = await generateBulkOrderTemplate(selectedShop.id, options);
    const buffer   = await workbook.xlsx.writeBuffer();
    const blob     = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `bulk_order_template_${selectedShop.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [selectedShop]);

  return {
    selectedShop,
    selectedShiftId,
    uploadedFile,
    parsedData,
    validationResults,
    submissionResults,
    isProcessing,
    processingStep,
    progress,
    userShops,
    selectedAssignUserIds,
    defaultProduct,
    useCityUnknown,
    duplicateCheckDays,
    columnMappings,
    duplicateRows,
    blacklistedRowNumbers,
    blacklistedCount: blacklistedRowNumbers.size,

    setSelectedShop,
    setSelectedShiftId,
    setSelectedAssignUserIds,
    setDefaultProduct,
    setUseCityUnknown,
    setDuplicateCheckDays,
    setColumnMappings,
    handleFileUpload,
    handleValidation,
    handleSubmit,
    handleRetryValidation,
    removeInvalidRow,
    removeAllDuplicates,
    removeBlacklistedRows,
    updateInvalidRow,
    clearAll,
    downloadTemplate,
    loadShops,
  };
};

export default useBulkOrder;